/**
 * Settings, Pharmacies (Treatment Plan spec v1.4, S-20).
 *
 * The clinic's linked pharmacies and their price lists, the price change
 * notices those lists send, and "Use my own cost". A list's prices reach only
 * the clinics linked to it (PS-016); a product MCOS has not confirmed never
 * shows here (PS-010). Contacts appear by name and role only (PH-009).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, Field, InfoHint, LoadingRows, PageHeader, Table, Td, Th, inputClass } from '../components/ui';

interface NoticeLine { itemId: string; name: string; oldCents: number | null; newCents: number | null; marginCents: number | null; note: string }
interface Notice { id: string; pharmacy: string; priceList: string; effectiveDate: string; createdAt: string; read: boolean; lines: NoticeLine[] }
interface Linked {
  pharmacyId: string;
  name: string;
  pharmacyStatus: 'invited' | 'active' | 'suspended';
  linkStatus: 'requested' | 'active';
  accountRef: string | null;
  priceList: { name: string } | null;
  liveVersion: { effectiveDate: string } | null;
  nextVersion: { effectiveDate: string } | null;
  contacts: Array<{ name: string; role: string | null; level: string; handles: string[] }>;
  portalReady: boolean;
}
interface Data { pharmacies: Linked[]; notices: Notice[]; directory: Array<{ pharmacyId: string; name: string }> }

const money = (cents: number | null) => (cents == null ? '-' : `$${(cents / 100).toFixed(2)}`);
const date = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const today = () => new Date().toISOString().slice(0, 10);
const HANDLES: Record<string, string> = { rx_issues: 'Rx issues', pricing: 'pricing', all: 'everything' };

function Chip({ tone, children }: { tone: 'green' | 'amber' | 'grey'; children: string }) {
  const cls = tone === 'green' ? 'bg-[#e3efe9] text-forest' : tone === 'amber' ? 'bg-[#fbf0d6] text-amber-deep' : 'bg-[#eef0f3] text-[#4d6072]';
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${cls}`}>{children}</span>;
}

function NoticeCard({ notice }: { notice: Notice }) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['clinic-pharmacies'] });
    void qc.invalidateQueries({ queryKey: ['nav-counts'] });
    void qc.invalidateQueries({ queryKey: ['opr-items'] });
  };
  const read = useMutation({ mutationFn: () => api.post(`/clinic-pharmacies/notices/${notice.id}/read`), onSuccess: refresh });
  const cost = useMutation({
    mutationFn: (v: { itemId: string; use: 'own' | 'price_list' }) => api.post(`/clinic-pharmacies/costs/${v.itemId}`, { use: v.use }),
    onSuccess: refresh,
  });
  const ahead = notice.effectiveDate > today();
  return (
    <Card
      title={`${notice.pharmacy} ${ahead ? 'changes' : 'changed'} its prices`}
      subtitle={`${notice.priceList} list, ${ahead ? 'takes effect' : 'in effect since'} ${date(notice.effectiveDate)}. Your patient prices, sent plans and accepted plans do not change.`}
      action={!notice.read ? <Button size="sm" variant="secondary" loading={read.isPending} onClick={() => read.mutate()}>Mark as read</Button> : <Chip tone="grey">Read</Chip>}
    >
      <Table minWidth={720}>
        <thead>
          <tr>
            <Th>Drug</Th>
            <Th align="right">Old cost</Th>
            <Th align="right">New cost</Th>
            <Th align="right">Margin at your price</Th>
            <Th>{''}</Th>
          </tr>
        </thead>
        <tbody>
          {notice.lines.map((l) => {
            const kept = /own cost is kept/i.test(l.note);
            const below = l.marginCents != null && l.marginCents < 0;
            return (
              <tr key={l.itemId}>
                <Td>
                  <div className="font-semibold text-navy">{l.name}</div>
                  {l.note && <div className={`text-[12px] ${below ? 'font-semibold text-alert' : 'text-[#4d6072]'}`}>{l.note}</div>}
                </Td>
                <Td align="right">{money(l.oldCents)}</Td>
                <Td align="right">{money(l.newCents)}</Td>
                <Td align="right" className={below ? 'font-semibold text-alert' : ''}>{money(l.marginCents)}</Td>
                <Td align="right">
                  {l.newCents != null && !/blocked in MCOS/i.test(l.note) && (
                    <Button size="sm" variant="ghost" loading={cost.isPending && cost.variables?.itemId === l.itemId} onClick={() => cost.mutate({ itemId: l.itemId, use: kept ? 'price_list' : 'own' })}>
                      {kept ? 'Use the list cost' : 'Use my own cost'}
                    </Button>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {cost.isSuccess && <p className="mt-2 text-[13px] text-forest" role="status">Saved. Your OPR uses the cost you chose.</p>}
      {cost.error && <p className="mt-2 text-sm text-red-600">{errorText(cost.error)}</p>}
    </Card>
  );
}

function LinkStatus({ p }: { p: Linked }) {
  if (p.pharmacyStatus === 'invited') return <Chip tone="amber">Invited, MCOS is checking its license</Chip>;
  if (p.linkStatus === 'requested') return <Chip tone="amber">Waiting for the pharmacy</Chip>;
  return <Chip tone="green">Linked</Chip>;
}

export function PharmaciesPage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['clinic-pharmacies'], queryFn: () => api.get<Data>('/clinic-pharmacies') });
  const [request, setRequest] = useState({ pharmacyId: '', accountRef: '' });
  const [invite, setInvite] = useState({ name: '', email: '', phone: '', licenseNumber: '', licenseState: '', accountRef: '' });
  const [inviting, setInviting] = useState(false);
  const done = (data: Data) => qc.setQueryData(['clinic-pharmacies'], (was: Data | undefined) => ({ ...data, directory: was?.directory.filter((d) => d.pharmacyId !== request.pharmacyId) ?? [] }));
  const ask = useMutation({ mutationFn: () => api.post<Data>('/clinic-pharmacies/request', request), onSuccess: (r) => { done(r); setRequest({ pharmacyId: '', accountRef: '' }); } });
  const send = useMutation({ mutationFn: () => api.post<Data>('/clinic-pharmacies/invite', invite), onSuccess: (r) => { done(r); setInviting(false); setInvite({ name: '', email: '', phone: '', licenseNumber: '', licenseState: '', accountRef: '' }); } });

  if (data.error) return <ErrorState error={data.error} retry={() => void data.refetch()} />;
  if (!data.data) return <LoadingRows rows={6} />;
  const unread = data.data.notices.filter((n) => !n.read);
  const readNotices = data.data.notices.filter((n) => n.read);

  return (
    <>
      <PageHeader title="Pharmacies" subtitle="The pharmacies you order from, and what their price lists mean for your costs." />
      <div className="space-y-6">
        {unread.map((n) => <NoticeCard key={n.id} notice={n} />)}

        <Card title="Your pharmacies">
          {data.data.pharmacies.length === 0 ? (
            <EmptyState title="No pharmacies linked yet" body="Link the pharmacy you order from. Its price list then sets your pharmacy cost for each drug, and a cost you type yourself is never overwritten." />
          ) : (
            <ul className="divide-y divide-hairline">
              {data.data.pharmacies.map((p) => (
                <li key={p.pharmacyId} className="py-4 first:pt-0">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-[19px] text-navy">{p.name}</p>
                      <p className="mt-0.5 text-[14px] text-[#4d6072]">
                        {p.priceList ? `${p.priceList.name} list` : 'Price list not assigned yet'}
                        {p.liveVersion ? `, current version from ${date(p.liveVersion.effectiveDate)}` : ''}
                        {p.nextVersion ? `, next on ${date(p.nextVersion.effectiveDate)}` : ''}
                        {p.accountRef ? `. Account ${p.accountRef}` : ''}
                      </p>
                    </div>
                    <LinkStatus p={p} />
                  </div>
                  {p.contacts.length > 0 && (
                    <p className="mt-2 text-[13px] text-[#4d6072]">
                      Contacts: {p.contacts.map((c) => `${c.name}${c.role ? `, ${c.role}` : ''} (${c.level}, ${c.handles.map((h) => HANDLES[h] ?? h).join(' and ')})`).join('; ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Link a pharmacy" subtitle="Ask a pharmacy on MCOS to link to your clinic. It accepts and assigns your price list.">
          <div className="grid items-end gap-3 md:grid-cols-[1fr_1fr_auto]">
            <Field label="Pharmacy">
              <select className={inputClass} value={request.pharmacyId} onChange={(e) => setRequest({ ...request, pharmacyId: e.target.value })}>
                <option value="">{data.data.directory.length ? 'Choose a pharmacy' : 'No other pharmacies on MCOS yet'}</option>
                {data.data.directory.map((d) => <option key={d.pharmacyId} value={d.pharmacyId}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Your account number there"><input className={inputClass} value={request.accountRef} onChange={(e) => setRequest({ ...request, accountRef: e.target.value })} /></Field>
            <Button loading={ask.isPending} disabled={!request.pharmacyId} onClick={() => ask.mutate()}>Ask to link</Button>
          </div>
          {ask.error && <p className="mt-2 text-sm text-red-600">{errorText(ask.error)}</p>}
          <div className="mt-5 border-t border-hairline pt-4">
            {!inviting ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="secondary" onClick={() => setInviting(true)}>Invite your pharmacy</Button>
                <InfoHint label="About inviting">Not on MCOS yet? Invite it. MCOS verifies the pharmacy license before it is activated, and no patient information reaches it until its BAA is signed.</InfoHint>
              </div>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-3">
                  <Field label="Pharmacy name"><input className={inputClass} value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></Field>
                  <Field label="Who to invite, email"><input className={inputClass} type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
                  <Field label="Main phone"><input className={inputClass} value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} /></Field>
                  <Field label="Pharmacy license number"><input className={inputClass} value={invite.licenseNumber} onChange={(e) => setInvite({ ...invite, licenseNumber: e.target.value })} /></Field>
                  <Field label="License state" hint="Two letters"><input className={inputClass} maxLength={2} value={invite.licenseState} onChange={(e) => setInvite({ ...invite, licenseState: e.target.value.toUpperCase() })} /></Field>
                  <Field label="Your account number there"><input className={inputClass} value={invite.accountRef} onChange={(e) => setInvite({ ...invite, accountRef: e.target.value })} /></Field>
                </div>
                {send.error && <p className="mt-2 text-sm text-red-600">{errorText(send.error)}</p>}
                <div className="mt-4 flex gap-2">
                  <Button loading={send.isPending} disabled={invite.name.trim().length < 2 || !invite.email.includes('@')} onClick={() => send.mutate()}>Send to MCOS</Button>
                  <Button variant="secondary" onClick={() => setInviting(false)}>Cancel</Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {readNotices.length > 0 && (
          <Card title="Earlier price changes">
            <ul className="divide-y divide-hairline text-[14px]">
              {readNotices.map((n) => (
                <li key={n.id} className="flex flex-wrap justify-between gap-2 py-2.5">
                  <span className="text-navy">{n.pharmacy}, {n.priceList}</span>
                  <span className="text-[#4d6072]">{n.lines.length} drug{n.lines.length === 1 ? '' : 's'}, effective {date(n.effectiveDate)}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </>
  );
}
